import type {
  FetchMenuTopRowProps,
  JaneDM,
  SmartSort,
} from '@iheartjane/dm-sdk';
import type { QueryFunctionContext } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';

import type { AlgoliaProduct } from '@jane/search/types';
import { useSDK } from '@jane/shared-ecomm/providers';
import type { MenuCategoryPath } from '@jane/shared/types';

type SharedRowProps<T> = T & Omit<FetchMenuTopRowProps, 'excludeCreativeIds'>;

type UseMenuTopRowQueryProps = SharedRowProps<{
  enabled: boolean;
  previousData: SmartSort<AlgoliaProduct> | undefined;
}>;

type UseMenuTopRowProps = SharedRowProps<{
  enabled: boolean;
  enableRefetch?: boolean;
  isTimerFinished: boolean;
  pageCategory?: MenuCategoryPath;
  pauseTimer: () => void;
  restartTimer: () => void;
}>;

const menuTopRowKeys = {
  all: ['sponsoredRow'] as const,
  list: (searchFilter: string | undefined, storeId: number) =>
    [...menuTopRowKeys.all, searchFilter, storeId] as const,
};

interface FetchMenuTopRowResponse {
  instance: SmartSort<AlgoliaProduct> | undefined;
}

const fetchMenuTopRow = async (
  context: QueryFunctionContext<ReturnType<(typeof menuTopRowKeys)['list']>>,
  sdk: JaneDM | null,
  row: SmartSort<AlgoliaProduct> | undefined
): Promise<FetchMenuTopRowResponse> => {
  const [, searchFilter, storeId] = context.queryKey;

  const instance = await sdk?.fetchMenuTopRow<
    AlgoliaProduct,
    { excludeCreativeIds: number[] | undefined }
  >({
    excludeCreativeIds: row?.['creativeIds'],
    searchAttributes: ['*'],
    searchFilter,
    storeId: Number(storeId),
  });

  return { instance };
};

const useMenuTopRowQuery = (props: UseMenuTopRowQueryProps) => {
  const { previousData, storeId, searchFilter, enabled } = props;
  const { sdk } = useSDK();
  const { data, isError, isFetching, isSuccess, refetch } = useQuery({
    enabled,
    queryFn: async (context) =>
      await fetchMenuTopRow(context, sdk, previousData),
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: menuTopRowKeys.list(searchFilter, storeId),
  });

  return {
    isError,
    isFetching,
    isSuccess,
    refetch,
    rowInstance: data?.instance,
  };
};

interface UseMenuTopRowResponse {
  isError: boolean;
  isFetching: boolean;
  isSuccess: boolean;
  rowInstance: SmartSort<AlgoliaProduct> | undefined;
}

export const useMenuTopRowWithRefresh = ({
  enableRefetch = true,
  isTimerFinished,
  pageCategory,
  restartTimer,
  pauseTimer,
  searchFilter,
  enabled,
  ...props
}: UseMenuTopRowProps): UseMenuTopRowResponse => {
  const [menuTopRow, setMenuTopRow] = useState<
    SmartSort<AlgoliaProduct> | undefined
  >(undefined);
  const [shouldContinueRefetching, setShouldContinueRefetching] =
    useState(true);

  /**
   * Query for sponsored products
   */
  const {
    rowInstance,
    isError,
    isFetching,
    isSuccess,
    refetch: refetchRowInstance,
  } = useMenuTopRowQuery({
    enabled: false, // Use refetch to run the query
    previousData: menuTopRow,
    searchFilter,
    ...props,
  });

  const refetchInstance = useCallback(async () => {
    const { data } = await refetchRowInstance();

    if (data?.instance && data.instance.products.length > 0) {
      setMenuTopRow(data.instance);
    } else {
      setShouldContinueRefetching(false);
      pauseTimer();
    }
  }, [refetchRowInstance]);

  /**
   * Restart timer and refetch on page change/load
   */
  useEffect(() => {
    if (enabled) {
      restartTimer();
      refetchInstance();
    }
  }, [pageCategory, searchFilter, enabled]);

  /**
   * Restart the refresh timer when time has elapsed
   */
  useEffect(() => {
    if (isTimerFinished && enabled) {
      if (enableRefetch && shouldContinueRefetching) {
        refetchInstance();
      }
      restartTimer();
    }
  }, [isTimerFinished, enableRefetch, enabled]);

  /**
   * Check to make sure we should render the row
   */
  const isRowValid = menuTopRow?.products?.length ?? -1 > 0;

  return {
    isError,
    isFetching,
    isSuccess,
    rowInstance: isRowValid ? menuTopRow : rowInstance,
  };
};
